import React from 'react';
import styled from 'styled-components';

import { GeoidModel } from '../../../../../../typings/api/skymap/rest/v1/.common';
import { CreateGeoidParameters } from '../../../../../../typings/api/skymap/rest/v1/geoid';
import { TokenService } from '../../../../services/authentication/token.service';
import { SkyMapAxiosServiceFactory } from '../../../../services/axios/skymap-axios-service-factory';
import { FileUpload } from '../../../../services/network/file-upload';
import { WebApi } from '../../../../services/web-api';
import { RequestParamsToApiPaths } from '../../../../utils/typescript-utils';
import { useErrorHandling } from '../../../hooks/use-error-handling';
import { Button } from '../../button/button';
import { Dialog } from '../../dialog/dialog';
import { FileSelectionButton } from '../../file-selection-button/file-selection-button';
import { InfoBox } from '../../info-box/info-box';
import { ProgressBar } from '../../progress-bar/progress-bar';

enum Status {
  Idle,
  Pending,
  Error,
  ResponseError,
}

interface Props {
  onClose: (geoidModel?: GeoidModel) => void;
}

const AddGeoidDialog = (props: Props) => {
  const [status, setStatus] = React.useState(Status.Idle);

  const [file, setFile] = React.useState<File>();
  const [bytesUploaded, setBytesUploaded] = React.useState<number>(0);
  const { buildErrorList, handleError, includeErrorPaths } =
    useErrorHandling<RequestParamsToApiPaths<CreateGeoidParameters>>();

  const cancelCloseButtonClick = () => {
    props.onClose();
  };

  const onGeoidAdded = async (files: File[]) => {
    try {
      setFile(files[0]);

      const upload = new FileUpload({
        apiBaseUrl: WebApi.getUrl(),
        authToken: new TokenService().get(),
        file: files[0],
        concurrency: 3,
      });

      upload.onProgress(setBytesUploaded);
      const result = await upload.start();
      const uploadId = result.id;

      const response = await SkyMapAxiosServiceFactory.instance.createGeoidServiceV1().createGeoid({
        body: { name: files[0].name, uploadId },
      });

      props.onClose(response.data);
    } catch (err: unknown) {
      setStatus(Status.Error);
      handleError(err);
    } finally {
      setFile(undefined);
    }
  };

  return (
    <Dialog
      closeIcon={status !== Status.Pending}
      closeOnDimmerClick={true}
      useGoogleTranslate={true}
      width={500}
      onClose={cancelCloseButtonClick}
    >
      {{
        header: 'Lägg till geoidmodell',
        content: (
          <Content>
            <p>
              Klicka på <strong>Välj fil</strong> för att ladda upp en ny geoidmodell. Observera att
              filen måste vara i TIFF-format (<strong>.tif</strong> - även känt som{' '}
              <strong>GeoTIFF</strong>).
            </p>

            <p>
              Geoidmodellen kommer automatiskt att vara dold, men kan väljas av en
              organisationsadministratör.
            </p>

            <InfoBox color="yellow" topMargin={true}>
              Har du endast en GRID-fil kan du konvertera denna till GeoTIFF med Metashape. Beroende
              på leverantör kan en GRID-fil exempelvis ha filändelsen <strong>.grd</strong>,{' '}
              <strong>.txt</strong>, <strong>gri</strong> eller <strong>.ascii</strong>.
            </InfoBox>

            <InfoBox
              color="yellow"
              leftIcon={{ icon: ['fad', 'sort-alt'], transform: { rotate: 90 } }}
              title="Så här konverterar du en GRID-fil till GeoTIFF"
              topMargin={true}
            >
              <ul>
                <li>
                  Ändra filändelse till <strong>.grd</strong>.
                </li>
                <li>
                  Läs in filen i MetaShape som <strong>ASCII Grid (*.grd)</strong>.
                </li>
                <li>
                  Resulterande <strong>TIFF-fil</strong> sparas därefter i MetaShapes AppData- eller
                  installationsmapp.
                </li>
              </ul>
            </InfoBox>

            {buildErrorList({
              filter: includeErrorPaths(['body.uploadId']),
              defaultMessage: 'Det gick inte att ladda upp filen.',
              infoBoxProps: {
                topMargin: false,
              },
            })}
          </Content>
        ),
        footer: {
          right: (
            <>
              {file ? (
                <ProgressBar
                  max={file.size}
                  maxPercentage={99}
                  min={0}
                  value={bytesUploaded}
                  width={'100px'}
                />
              ) : (
                <FileSelectionButton
                  accept=".tif"
                  color="primary"
                  multiple={false}
                  text="Välj fil"
                  variant="contained"
                  onFilesSelected={onGeoidAdded}
                />
              )}
              <Button
                disabled={status === Status.Pending}
                variant="text"
                onClick={cancelCloseButtonClick}
              >
                Stäng
              </Button>
            </>
          ),
        },
      }}
    </Dialog>
  );
};

const Content = styled.div`
  display: flex;
  flex-direction: column;

  ul {
    list-style-type: numeric;
    padding-left: 1em;
  }

  strong {
    font-weight: 500;
  }

  p {
    margin: 0.25em 0;
  }
`;

export { AddGeoidDialog };
